import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import './i18n';

// Components
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import Journal from './components/Journal';
import Activities from './components/Activities';
import Auth from './components/Auth';
import Landing from './components/Landing';
import DailyPromptModal from './components/DailyPromptModal';
import SpotifyCallback from './components/SpotifyCallback';
import AuthCallback from './components/AuthCallback';
import AdminDashboard from './components/AdminDashboard';
import LanguagePicker from './components/LanguagePicker';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.baseURL = API;
axios.defaults.withCredentials = true;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter({ children }) {
  const location = useLocation();
  // Synchronous detection during render to avoid race with ProtectedRoute logic
  if (location.hash?.includes('session_id=')) {
    return children.authCallback;
  }
  return children.routes;
}

function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showPromptModal, setShowPromptModal] = useState(false);

  // Check session: prefer cookie-based session (Google) via /auth/me; fallback to JWT
  useEffect(() => {
    // If returning from OAuth callback, skip the /me check (AuthCallback will set session)
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const init = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const res = await axios.get('/auth/me', { headers, withCredentials: true });
        setUser(res.data);
        // Seed UI language from server-side stored preference,
        // but NEVER override an explicit user pick (i18n_picked=1).
        const pref = res.data?.preferred_language;
        const userPicked = localStorage.getItem('i18n_picked') === '1';
        if (!userPicked && pref && ['en', 'es'].includes(pref) && pref !== localStorage.getItem('i18nextLng')) {
          const i18n = (await import('./i18n')).default;
          i18n.changeLanguage(pref);
          localStorage.setItem('i18nextLng', pref);
          localStorage.setItem('i18n_picked', '1');
        } else if (userPicked) {
          // Push the user's explicit choice to backend so subsequent sessions are consistent
          const current = localStorage.getItem('i18nextLng');
          if (current && current !== pref) {
            axios.patch('/auth/me/preferences', { preferred_language: current }, { withCredentials: true }).catch(() => {});
          }
        }
      } catch (e) {
        if (token) {
          // token invalid -> clear
          localStorage.removeItem('token');
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  const login = (newToken, userData) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    }
    setUser(userData);
    toast.success('Welcome to your wellness journey!');

    const today = new Date().toDateString();
    const lastPromptShown = localStorage.getItem('promptModalShown');
    if (lastPromptShown !== today) {
      // Delay so the user sees the dashboard first before the modal pops
      setTimeout(() => setShowPromptModal(true), 4500);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout', {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.info('Take care, see you soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const authedRoutes = (
    <>
      <Navbar user={user} logout={logout} />
      <main className="pt-16">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/activities" element={<Activities />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/spotify/callback" element={<SpotifyCallback />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      <DailyPromptModal isOpen={showPromptModal} onClose={() => setShowPromptModal(false)} />
    </>
  );

  const publicRoutes = (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth onAuth={login} />} />
      <Route path="/spotify/callback" element={<SpotifyCallback />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  return (
    <Router>
      <div className="App min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-amber-50">
        <Toaster position="top-right" />
        <LanguagePicker />
        <AppRouter>
          {{
            authCallback: <AuthCallback onAuth={login} />,
            routes: user ? authedRoutes : publicRoutes,
          }}
        </AppRouter>
      </div>
    </Router>
  );
}

export default App;
